import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgxAuthService } from '@go-modules/services/auth/auth.service';
import { TranslateModule } from '@ngx-translate/core';
import { AuthTemplateModule } from '@go-modules/components/auth-template/auth-template.module';
import { GoMaterialModule } from '@go-modules/go-material.module';
import { CommonModule } from '@angular/common';
import type { CourseIdValidationDataInterface } from '@dashboard/app/interfaces/auth-response-course-id.interface';
import type { InviteValidation } from '@dashboard/app/interfaces/invite-validation.interface';
import { GroupType } from '@go-modules/enums/group-type';
import { BehaviorSubject } from 'rxjs';
import { ROUTE_NAMES, RouterService } from '@go-modules/services/router/router.service';
import { SignupStateService } from '@dashboard/app/services/signup-state.service';

@Component({
	selector: 'ngx-group-confirm',
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		TranslateModule,
		GoMaterialModule,
		AuthTemplateModule
	],
	templateUrl: './group-confirm.component.html',
	styleUrls: ['./group-confirm.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class GroupConfirmComponent implements OnInit {
	public group$: BehaviorSubject<CourseIdValidationDataInterface> =
		new BehaviorSubject<CourseIdValidationDataInterface>(null);
	public invite$: BehaviorSubject<InviteValidation> = new BehaviorSubject<InviteValidation>(null);
	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public error$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public showConfirmation$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	constructor (
		private authService: NgxAuthService,
		private routerService: RouterService,
		private signupStateService: SignupStateService
	) {}

	public ngOnInit () {
		const { invite, course } = this.routerService.getCurrentQueryParams();
		if (invite || course) {
			this.handleInvite();
		}
	}

	public get headerTranslationKey () {
		const keys = {
			course: 'sign-up-heading-course-confirmation',
			org: 'sign-up-heading-org-confirmation',
			account: 'sign-up-heading-account-confirmation'
		};

		return keys[this.group$.value.group_type];
	}

	public get subHeaderTranslationKey () {
		const keys = {
			course: 'sign-up-sub-heading-course-confirmation',
			org: 'sign-up-sub-heading-org-confirmation',
			account: 'sign-up-sub-heading-account-confirmation'
		};

		return keys[this.group$.value.group_type];
	}

	public get getGroupTypeNameTranslation () {
		const keys = {
			course: 'sign-up-confirmation-course-label',
			org: 'sign-up-confirmation-org-label',
			account: 'sign-up-confirmation-account-label'
		};

		return keys[this.group$.value.group_type];
	}

	public get isCourseInvite () {
		return this.group$.value.group_type === GroupType.COURSE;
	}

	public handleInvite () {
		this.loading$.next(true);
		const { invite, course } = this.routerService.getCurrentQueryParams();
		if (invite) {
			this.authService.validateInvite(invite)
				.subscribe({
					next: (response) => {
						this.invite$.next(response);
						this.validateGroup(this.invite$.value.group_uuid);
					},
					error: (error) => {
						this.loading$.next(false);
						this.error$.next(true);
						console.error(error);
					}
				});
		} else if (course) {
			this.validateGroup(course);
		}
	}

	public validateGroup (uuid: string) {
		this.authService.validateGroupCode(uuid)
			.subscribe({
				next: (response) => {
					this.group$.next(response);
					this.showConfirmation$.next(true);
					this.loading$.next(false);
				},
				error: (error) => {
					this.error$.next(true);
					this.loading$.next(false);
					console.error(error);
				}
			});
	}

	public confirmGroup () {
		if (this.authService.isActive()) {
			this.authService.getCurrentUser().subscribe((result) => {
				// checks if user and invite match before adding logged in user to course
				const { invite, course, redirect } = this.routerService.getCurrentQueryParams();
				if (invite && (result.email !== this.invite$.value.email)) {
					this.error$.next(true);
					this.showConfirmation$.next(false);
					return;
				}

				const options = {
					inviteId: invite || course,
					redirect,
					groupId: this.group$.value.group_id
				};

				this.authService.setUpUser(result, options)
					.subscribe({
						next: (state) => {
							if (state) {
								this.routerService.navigateTo(state.name as ROUTE_NAMES, state.params, state.options);
							}
						},
						error: () => {
							this.error$.next(true);
							this.showConfirmation$.next(false);
						}
					});
			});
		} else {
			// Store the course invite data in the service before navigating
			this.signupStateService.setCourseInvite({
				course: this.group$.value,
				invite: this.invite$.value
			});
			this.routerService.navigateTo(ROUTE_NAMES.AUTH_SIGNUP, {}, { skipLocationChange: true });
		}
	}

	public toLogin (): void {
		if(this.authService.isActive()) {
			this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_ROOT);
		} else {
			this.routerService.navigateTo(ROUTE_NAMES.AUTH_LOGIN, {}, {queryParams: {}});
		}
	}
}
